"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ArrowRightIcon, Quote, Star } from "lucide-react";
import { motion, useAnimation, useInView } from "framer-motion";
import { useEffect, useRef, useState, useCallback } from "react";
import Image from "next/image";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "@heroui/react";
import DottedBackground from "./dotted-background";
import { DottedVignetteBackground } from "./dotted-vignette-background";
import { useTheme } from "next-themes";
import ReactPlayer from "react-player";
export interface Features {
  id: number;
  name: string;
  description: string;
  buttonPreText?: string;
  buttonText?: string;
  buttonLink?: string;
  video: string;
  stillImage?: string;
  bannerImage?: string;
}
export interface AnimatedFeaturesProps {
  title?: string;
  description?: string;
  subtitle?: string;
  badgeIcon?: React.ReactNode;
  badgeText?: string;
  features?: Features[];
  autoRotateInterval?: number;
  trustedCompanies?: string[];
  trustedCompaniesTitle?: string;
  className?: string;
}
export function AnimatedFeatures({
  title = "Loved by the community",
  description = "Don't just take our word for it. See what developers and companies have to say about our starter template.",
  subtitle = "Don't just take our word for it. See what developers and companies have to say about our starter template.",
  badgeIcon,
  badgeText,
  features = [],
  autoRotateInterval = 6000,
  className
}: AnimatedFeaturesProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [videosLoaded, setVideosLoaded] = useState<Set<number>>(new Set());

  // Single video player ref for better memory management
  const videoPlayerRef = useRef<ReactPlayer | null>(null);

  // Refs for scroll animations
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, {
    once: true,
    amount: 0.2
  });
  const controls = useAnimation();
  const {
    resolvedTheme
  } = useTheme();
  const [isDark, setIsDark] = useState<boolean | undefined>(undefined);

  // Update isDark when theme changes
  useEffect(() => {
    if (resolvedTheme) {
      setIsDark(resolvedTheme === "dark");
    }
  }, [resolvedTheme]);

  // Animation variants
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  // Trigger animations when section comes into view
  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  // Handle video ready - track loaded videos
  const handleVideoReady = useCallback(() => {
    setVideosLoaded(prev => new Set(prev).add(activeIndex));
  }, [activeIndex]);

  // Handle video error - fallback to still image
  const handleVideoError = useCallback((error: any) => {
    console.warn("[AnimatedFeatures] Video error:", error);
  }, []);

  // Handle manual card click with debouncing
  const handleCardClick = useCallback((index: number) => {
    if (index === activeIndex) return; // No need to change if already active

    // Clear auto-rotation when manually clicked
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    setActiveIndex(index);
    setIsAutoRotating(false);

    // Resume auto-rotation after 10 seconds of inactivity
    const resumeTimer = setTimeout(() => {
      setIsAutoRotating(true);
    }, 10000);
    return () => clearTimeout(resumeTimer);
  }, [activeIndex, intervalId]);

  // Auto rotate features - optimized
  useEffect(() => {
    if (!isAutoRotating || autoRotateInterval <= 0 || features.length <= 1) {
      return;
    }
    const interval = setInterval(() => {
      setActiveIndex(current => (current + 1) % features.length);
    }, autoRotateInterval);
    setIntervalId(interval);
    return () => clearInterval(interval);
  }, [isAutoRotating, autoRotateInterval, features.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  // Preload next video for smoother transitions
  useEffect(() => {
    if (features.length > 1) {
      const nextIndex = (activeIndex + 1) % features.length;
      const nextVideo = features[nextIndex]?.video;
      if (nextVideo && !videosLoaded.has(nextIndex)) {
        // Preload next video
        const link = document.createElement("link");
        link.rel = "prefetch";
        link.href = nextVideo;
        document.head.appendChild(link);

        // Cleanup
        return () => {
          document.head.removeChild(link);
        };
      }
    }
  }, [activeIndex, features, videosLoaded]);
  if (features.length === 0) {
    return null;
  }
  const currentFeature = features[activeIndex];
  return <section ref={sectionRef} id="features" className={`w-screen overflow-visible px-4 py-16 sm:px-6 lg:px-10 lg:py-24 ${className || ""}`} data-sentry-component="AnimatedFeatures" data-sentry-source-file="animated-feature.tsx">
      <div className="mx-auto max-w-7xl">
        <motion.div initial="hidden" animate={controls} variants={containerVariants} className="grid w-full grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 xl:gap-24" data-sentry-element="unknown" data-sentry-source-file="animated-feature.tsx">
          {/* Left side: Heading and navigation */}
          <motion.div variants={itemVariants} className="flex flex-col items-center justify-center space-y-8 xl:items-start" data-sentry-element="unknown" data-sentry-source-file="animated-feature.tsx">
            <div className="space-y-6">
              {badgeText && <div className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                  {badgeIcon}
                  <span>{badgeText}</span>
                </div>}

              <h2 className="text-2xl font-semibold tracking-tighter sm:text-3xl lg:text-4xl">
                {title}
              </h2>

              <p className="max-w-[600px] text-base text-muted-foreground sm:text-lg md:text-xl/relaxed">
                {description}
              </p>
            </div>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {features.map((feature, index) => <Card key={feature.id} onClick={() => handleCardClick(index)} className={`group max-h-[250px] min-h-[235px] min-w-[290px] max-w-[300px] cursor-pointer rounded-2xl border transition-all duration-300 hover:shadow-md ${activeIndex === index ? "border-primary/50 bg-card shadow-md" : "border-border bg-card/50 hover:border-primary/30"}`}>
                  <CardContent className="flex flex-col items-center p-4">
                    <div className="mb-3 flex justify-between overflow-visible rounded-lg">
                      <Image src={feature.bannerImage || "/images/card-banner-image.png"} alt={feature.name} width={260} height={60} className="h-auto w-full rounded-lg object-cover transition-transform duration-300 group-hover:scale-[103%]" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-base font-semibold leading-tight tracking-tight sm:text-lg">
                        {feature.name}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                        {feature.description}
                      </p>
                    </div>
                    {feature.buttonLink && <Button variant="light" size="sm" className="group/button mt-2 flex flex-row items-center justify-center text-sm" radius="full" color="primary" endContent={<ArrowRightIcon className="h-4 w-4 transition-transform duration-300 group-hover/button:translate-x-0.5" />}>
                        {feature.buttonPreText ? <span className="text-xs">
                            {feature.buttonPreText} -{" "}
                          </span> : null}
                        {feature.buttonText}
                      </Button>}
                  </CardContent>
                </Card>)}
            </div>

            {subtitle && <p className="text-sm text-foreground/80 sm:text-base">
                {subtitle}
              </p>}
          </motion.div>

          {/* Right side: Single video player */}
          <motion.div variants={itemVariants} className="relative h-full min-h-[400px] overflow-visible p-10 lg:my-auto lg:h-[50%] xl:h-full xl:min-h-[500px]" data-sentry-element="unknown" data-sentry-source-file="animated-feature.tsx">
            <DottedVignetteBackground dotColor="#f50" backgroundColor={isDark === undefined ? "transparent" : isDark ? "#09090B" : "#ffffff"} innerGlowColor={isDark === undefined ? "transparent" : isDark ? "#09090B" : "#ffffff"} vignetteColor={isDark === undefined ? "transparent" : isDark ? "#09090B" : "#ffffff"} enableVignette={true} dotSpacing={5} className="opacity-60 dark:opacity-50" data-sentry-element="DottedVignetteBackground" data-sentry-source-file="animated-feature.tsx" />
            = {/* Gradient overlays - behind video player */}
            <div className="pointer-events-none absolute inset-0 z-10 bg-radial-10 to-white dark:to-[#09090B]" />
            <div className="absolute bottom-0 left-0 right-0 z-20 h-10 w-full bg-gradient-to-t from-white via-transparent to-transparent dark:from-[#09090B]"></div>
            <div className="absolute left-0 right-0 top-0 z-20 h-10 w-full bg-gradient-to-b from-white via-transparent to-transparent dark:from-[#09090B]"></div>
            <div className="absolute bottom-0 left-0 top-0 z-20 h-full w-10 bg-gradient-to-r from-white via-transparent to-transparent dark:from-[#09090B]"></div>
            <div className="absolute bottom-0 right-0 top-0 z-20 h-full w-10 bg-gradient-to-l from-white via-transparent to-transparent dark:from-[#09090B]"></div>
            {/* Single video player with smooth transitions - on top */}
            <motion.div className="absolute inset-0 z-30" key={activeIndex} // Only re-render when activeIndex changes
          initial={{
            opacity: 0,
            x: 100
          }} animate={{
            opacity: 1,
            x: 0,
            scale: 1
          }} transition={{
            duration: 0.5,
            ease: "easeInOut"
          }} data-sentry-element="unknown" data-sentry-source-file="animated-feature.tsx">
              <div className="flex h-full flex-col rounded-xl p-6 sm:p-8">
                <div className="relative mb-6 flex flex-1 items-center">
                  <div className="relative mx-auto mt-4 w-full max-w-full overflow-hidden rounded-xl sm:max-w-[530px] md:max-w-[707px] lg:min-w-[884px] lg:max-w-[884px]">
                    {/* Background still image - always visible */}
                    <div className="absolute inset-0 z-10 aspect-[1908/1080] overflow-hidden rounded-xl border-1 border-transparent transition-colors duration-1000 dark:border-white/20" style={{
                    backgroundImage: `url(${currentFeature.stillImage || currentFeature.bannerImage || "/images/card-banner-image.png"})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat"
                  }} />

                    {/* Video player on top */}
                    <ReactPlayer ref={videoPlayerRef} url={currentFeature.video} playing={isInView} loop={true} muted playsinline width="100%" height="auto" className="relative z-20 aspect-[1908/1080] overflow-hidden rounded-xl border-1 border-transparent transition-colors duration-1000 dark:border-white/20" config={{
                    file: {
                      attributes: {
                        preload: isInView ? "metadata" : "none",
                        "aria-label": "Feature demonstration video"
                      }
                    }
                  }} onReady={handleVideoReady} onError={handleVideoError} style={{
                    backgroundColor: "transparent"
                  }} data-sentry-element="ReactPlayer" data-sentry-source-file="animated-feature.tsx" />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}